// Ballpark cost pill shared by both roof-estimate surfaces (the SAM Quick Quote card and the
// property detail's Roof Estimate section) — mirrors RoofPitchPanel's role as a shared display
// piece for a value the backend computes once (app.services.pricing.quick_quote) and both
// surfaces render identically. Deliberately a range, not a single number: Quick Quote is a fast
// ballpark, never a priced Estimate (see estimate/plan1.md for why the two stay unconnected).
export interface QuickQuoteCostEstimateProps {
  low?: number | null;
  high?: number | null;
  unit?: string | null;
  billableQuantity?: number | null;
  pricePerUnit?: number | null;
  complexityFactor?: number | null;
}

const UNIT_LABEL: Record<string, string> = { sqft: 'sqft', square: 'square', sqm: 'sqm' };

/** Abbreviates to the nearest $0.1k above $1,000 (matches the "$11.8k" ballpark convention —
 *  a precise-looking full number would overstate this estimate's actual precision). */
const fmtK = (n: number): string =>
  n >= 1000 ? `$${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k` : `$${Math.round(n)}`;

export function QuickQuoteCostEstimate({ low, high, unit, billableQuantity, pricePerUnit, complexityFactor }: QuickQuoteCostEstimateProps) {
  if (low == null || high == null) return null;
  const unitLabel = unit ? UNIT_LABEL[unit] ?? unit : null;

  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-accent/20 bg-accent-soft px-3 py-2">
      <div>
        <p className="text-xs font-medium text-ui-text2">Estimated cost</p>
        {billableQuantity != null && unitLabel && pricePerUnit != null && (
          <p className="text-xs text-ui-text3">
            {billableQuantity.toLocaleString(undefined, { maximumFractionDigits: 1 })} {unitLabel} × ${pricePerUnit.toLocaleString()}/{unitLabel}
            {complexityFactor != null && complexityFactor !== 1 && <> × {complexityFactor.toLocaleString()} complexity</>}
          </p>
        )}
      </div>
      <p className="flex-shrink-0 text-sm font-bold text-accent-text">
        {fmtK(low)}{high > low ? `–${fmtK(high).replace('$', '')}` : ''}
      </p>
    </div>
  );
}

export default QuickQuoteCostEstimate;
